import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  BatchDetailLink,
  ShipmentDetailLink,
  WorkOrderDetailLink,
} from "@/components/hub/HubEntityDetailLink";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { manufacturingLocationShortLabel } from "@/lib/manufacturingLocationChip";
import {
  operationalLocationSelectLabel,
  transferEndpointLocationLabelById,
  useOperationalLocations,
} from "@/lib/transferEndpointLocations";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { cn, EM_DASH } from "@/lib/utils";

import {
  createStockingOrderBatch,
  createStockingShipmentTransferRequests,
  fetchStockingOrders,
  STOCKING_ORDERS_QUERY_KEY,
  type StockingOrderListRow,
  type StockingOrderProgress,
} from "./stockingOrdersApi";
import {
  buildStockingBatchRequestKey,
  defaultStockingTransferDestinationId,
  stockingOrderProgressLabel,
} from "./stockingOrdersPageModel";

const PAGE_HELP =
  "Stocking work orders build inventory without a customer sales order. Create an internal batch, complete the normal manufacturing shipment, then plan a separate Suntronic-to-SGS transfer.";

function progressTone(progress: StockingOrderProgress): "neutral" | "warning" | "success" | "danger" {
  if (progress === "complete") return "success";
  if (progress === "cancelled") return "danger";
  if (progress === "needs_batch" || progress === "needs_shipment" || progress === "needs_transfer_plan") {
    return "warning";
  }
  return "neutral";
}

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(year)}-${month}-${day}`;
}

export function StockingOrdersPage() {
  const canWrite = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const operationalLocations = useOperationalLocations();

  const [locationFilter, setLocationFilter] = useState<string>("");
  const [progressFilter, setProgressFilter] = useState<StockingOrderProgress | "all">("all");
  const [search, setSearch] = useState("");
  const [batchModalRow, setBatchModalRow] = useState<StockingOrderListRow | null>(null);
  const [transferModalShipmentId, setTransferModalShipmentId] = useState<number | null>(null);
  const [batchQuantity, setBatchQuantity] = useState("");
  const [batchDestinationId, setBatchDestinationId] = useState("");
  const [batchShipByDate, setBatchShipByDate] = useState("");
  const [transferTargetDate, setTransferTargetDate] = useState(todayIsoDate());
  const [transferNotes, setTransferNotes] = useState("");

  const locationId = locationFilter === "" ? null : Number(locationFilter);

  const listQuery = useQuery({
    queryKey: [...STOCKING_ORDERS_QUERY_KEY, locationId, progressFilter, search],
    queryFn: () =>
      fetchStockingOrders({
        locationId,
        progress: progressFilter,
        search,
      }),
  });

  const batchMutation = useMutation({
    mutationFn: createStockingOrderBatch,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STOCKING_ORDERS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers"] });
      setBatchModalRow(null);
      showToast({ message: "Stocking batch saved.", tone: "success" });
    },
  });

  const transferMutation = useMutation({
    mutationFn: createStockingShipmentTransferRequests,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STOCKING_ORDERS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers"] });
      setTransferModalShipmentId(null);
      showToast({ message: "Transfer request planned.", tone: "success" });
    },
  });

  const rows = listQuery.data?.rows ?? [];
  const listError =
    listQuery.error instanceof ApiError
      ? listQuery.error.message
      : listQuery.error instanceof Error
        ? listQuery.error.message
        : null;

  const openBatchModal = (row: StockingOrderListRow) => {
    const defaultDestination = defaultStockingTransferDestinationId(
      row.location_id,
      operationalLocations.locations,
    );
    setBatchModalRow(row);
    setBatchQuantity(String(Math.max(1, Math.trunc(row.remaining_quantity))));
    setBatchDestinationId(defaultDestination == null ? "" : String(defaultDestination));
    setBatchShipByDate("");
  };

  const actionError =
    batchMutation.error instanceof ApiError
      ? batchMutation.error.message
      : batchMutation.error instanceof Error
        ? batchMutation.error.message
        : transferMutation.error instanceof ApiError
          ? transferMutation.error.message
          : transferMutation.error instanceof Error
            ? transferMutation.error.message
            : null;

  const transferRow = useMemo(
    () =>
      rows.find((row) =>
        row.shipments.some((shipment) => shipment.shipment_id === transferModalShipmentId),
      ) ?? null,
    [rows, transferModalShipmentId],
  );

  const filtersActive =
    locationFilter !== "" || progressFilter !== "all" || search.trim() !== "";

  const stockingColumns = useMemo((): HubDataTableColumn<StockingOrderListRow>[] => {
    return [
      {
        id: "workOrder",
        header: "Work order",
        sortValue: (row) => row.build_order_number ?? String(row.build_order_id),
        cell: (row) => (
          <>
            <div className="font-mono text-xs">
              <WorkOrderDetailLink buildOrderId={row.build_order_id}>
                {row.build_order_number ?? String(row.build_order_id)}
              </WorkOrderDetailLink>
            </div>
            {row.cm_purchase_order_number ? (
              <div className="mt-1 text-[11px] text-slate-600">CM PO {row.cm_purchase_order_number}</div>
            ) : null}
          </>
        ),
      },
      {
        id: "sku",
        header: "SKU",
        overflow: "nowrap",
        sortValue: (row) => row.sku,
        cell: (row) => <span className="font-mono text-xs">{row.sku}</span>,
      },
      {
        id: "site",
        header: "Site",
        sortValue: (row) => row.location_abbreviation ?? row.location_name ?? "",
        cell: (row) =>
          manufacturingLocationShortLabel(row.location_id, row.location_abbreviation ?? row.location_name),
      },
      {
        id: "ordered",
        header: "Ordered",
        align: "right",
        sortValue: (row) => row.ordered_quantity,
        cell: (row) => row.ordered_quantity,
      },
      {
        id: "received",
        header: "Received",
        align: "right",
        sortValue: (row) => row.received_quantity,
        cell: (row) => row.received_quantity,
      },
      {
        id: "batch",
        header: "Batch",
        sortValue: (row) => row.batch_id ?? -1,
        cell: (row) => (row.batch_id != null ? <BatchDetailLink batchId={row.batch_id} /> : EM_DASH),
      },
      {
        id: "shipment",
        header: "Shipment",
        sortValue: (row) => row.shipments[0]?.shipment_id ?? -1,
        cell: (row) => {
          const latestShipment = row.shipments[0] ?? null;
          return latestShipment != null ? (
            <ShipmentDetailLink shipmentId={latestShipment.shipment_id} className="text-xs" />
          ) : (
            EM_DASH
          );
        },
      },
      {
        id: "destination",
        header: "Destination",
        sortValue: (row) => row.transfer_destination_name ?? "",
        cell: (row) =>
          row.transfer_destination_name ??
          (row.transfer_destination_location_id != null
            ? transferEndpointLocationLabelById(
                operationalLocations.locations,
                row.transfer_destination_location_id,
              )
            : EM_DASH),
      },
      {
        id: "progress",
        header: "Progress",
        sortValue: (row) => row.progress,
        cell: (row) => (
          <Badge tone={progressTone(row.progress)}>{stockingOrderProgressLabel(row.progress)}</Badge>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        sortValue: () => "",
        cell: (row) => {
          const latestShipment = row.shipments[0] ?? null;
          const canPlanTransfer =
            latestShipment != null &&
            latestShipment.date_shipped != null &&
            latestShipment.transfer_request_id == null;
          return (
            <div className="flex flex-wrap gap-2">
              {row.progress === "needs_batch" || row.remaining_quantity > 0 ? (
                <ReadOnlyHoverLayer active={!canWrite} hoverHint="Manufacturing management write access required.">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!canWrite || batchMutation.isPending}
                    onClick={() => openBatchModal(row)}
                  >
                    Create batch
                  </Button>
                </ReadOnlyHoverLayer>
              ) : null}
              {canPlanTransfer ? (
                <ReadOnlyHoverLayer active={!canWrite} hoverHint="Manufacturing management write access required.">
                  <Button
                    size="sm"
                    disabled={!canWrite || transferMutation.isPending}
                    onClick={() => {
                      setTransferModalShipmentId(latestShipment.shipment_id);
                      setTransferTargetDate(todayIsoDate());
                      setTransferNotes("");
                    }}
                  >
                    Plan transfer
                  </Button>
                </ReadOnlyHoverLayer>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [
    batchMutation.isPending,
    canWrite,
    openBatchModal,
    operationalLocations.locations,
    transferMutation.isPending,
  ]);

  return (
    <div className="space-y-4 pb-8">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Stocking orders</PageTitleWithHelp>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem]">
          <Label htmlFor="stocking-location-filter">Site</Label>
          <select
            id="stocking-location-filter"
            className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={locationFilter}
            onChange={(event) => setLocationFilter(event.target.value)}
          >
            <option value="">All sites</option>
            {operationalLocations.locations.map((location) => (
              <option key={location.id} value={String(location.id)}>
                {operationalLocationSelectLabel(location)}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[12rem]">
          <Label htmlFor="stocking-progress-filter">Progress</Label>
          <select
            id="stocking-progress-filter"
            className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={progressFilter}
            onChange={(event) => setProgressFilter(event.target.value as StockingOrderProgress | "all")}
          >
            <option value="all">All progress</option>
            <option value="needs_batch">Needs batch</option>
            <option value="needs_shipment">Needs shipment</option>
            <option value="needs_transfer_plan">Needs transfer plan</option>
            <option value="transfer_planned">Transfer planned</option>
            <option value="transfer_allocated">Transfer allocated</option>
            <option value="complete">Complete</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="min-w-[14rem] flex-1">
          <Label htmlFor="stocking-search">Search</Label>
          <Input
            id="stocking-search"
            className="mt-1"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Work order, SKU, CM PO"
          />
        </div>
        <div className="flex flex-wrap gap-2 pb-0.5">
          <Link
            to="/manufacturing-management/cm-po"
            className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Contract manufacturing purchase orders
          </Link>
          <Link
            to="/manufacturing-management/work-orders"
            className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Work orders
          </Link>
          <Link
            to="/manufacturing-management/transfers"
            className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Transfers
          </Link>
        </div>
      </div>

      {listError ? <p className="text-sm text-red-700">{listError}</p> : null}
      {actionError ? <p className="text-sm text-red-700">{actionError}</p> : null}

      <HubDataTable
        title={
          listQuery.isLoading
            ? "Loading stocking orders…"
            : `${rows.length} stocking order${rows.length === 1 ? "" : "s"}`
        }
        columns={stockingColumns}
        rows={rows}
        rowKey={(row) => row.build_order_id}
        loading={listQuery.isLoading}
        loadingMessage="Loading stocking orders…"
        error={listError}
        empty="No stocking work orders."
        isFiltered={filtersActive}
        filteredEmpty="No stocking work orders match the current filters."
        tableClassName="min-w-full text-sm"
      />

      {batchModalRow != null ? (
        <div
          {...hubModalOverlayProps(false)}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">Create internal batch</h2>
            <p className="mt-1 text-sm text-slate-600">
              Work order {batchModalRow.build_order_number ?? String(batchModalRow.build_order_id)} · {batchModalRow.sku}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="batch-quantity">Quantity</Label>
                <Input
                  id="batch-quantity"
                  className="mt-1"
                  value={batchQuantity}
                  onChange={(event) => setBatchQuantity(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="batch-destination">Destination site</Label>
                <select
                  id="batch-destination"
                  className={cn("mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm")}
                  value={batchDestinationId}
                  onChange={(event) => setBatchDestinationId(event.target.value)}
                >
                  <option value="">Select destination</option>
                  {operationalLocations.locations
                    .filter((location) => location.id !== batchModalRow.location_id)
                    .map((location) => (
                      <option key={location.id} value={String(location.id)}>
                        {operationalLocationSelectLabel(location)}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <Label htmlFor="batch-ship-by">Manufacturer ship by (optional)</Label>
                <Input
                  id="batch-ship-by"
                  className="mt-1"
                  type="date"
                  value={batchShipByDate}
                  onChange={(event) => setBatchShipByDate(event.target.value)}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setBatchModalRow(null)} disabled={batchMutation.isPending}>
                Cancel
              </Button>
              <Button
                disabled={batchMutation.isPending}
                onClick={() => {
                  const quantity = Number(batchQuantity);
                  const destinationLocationId = Number(batchDestinationId);
                  if (!Number.isInteger(quantity) || quantity <= 0 || !Number.isInteger(destinationLocationId)) {
                    return;
                  }
                  void batchMutation.mutateAsync({
                    buildOrderId: batchModalRow.build_order_id,
                    quantity,
                    destinationLocationId,
                    requestKey: buildStockingBatchRequestKey(
                      batchModalRow.build_order_id,
                      quantity,
                      destinationLocationId,
                    ),
                    mfrShipByDate: batchShipByDate === "" ? null : batchShipByDate,
                  });
                }}
              >
                Create batch
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {transferModalShipmentId != null ? (
        <div
          {...hubModalOverlayProps(false)}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">Plan transfer</h2>
            <p className="mt-1 text-sm text-slate-600">
              Shipment {String(transferModalShipmentId)}
              {transferRow?.sku ? ` · ${transferRow.sku}` : ""}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="transfer-target-date">Target date</Label>
                <Input
                  id="transfer-target-date"
                  className="mt-1"
                  type="date"
                  value={transferTargetDate}
                  onChange={(event) => setTransferTargetDate(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="transfer-notes">Notes (optional)</Label>
                <Input
                  id="transfer-notes"
                  className="mt-1"
                  value={transferNotes}
                  onChange={(event) => setTransferNotes(event.target.value)}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTransferModalShipmentId(null)} disabled={transferMutation.isPending}>
                Cancel
              </Button>
              <Button
                disabled={transferMutation.isPending || transferTargetDate.trim() === ""}
                onClick={() => {
                  void transferMutation.mutateAsync({
                    shipmentId: transferModalShipmentId,
                    targetDate: transferTargetDate,
                    notes: transferNotes.trim() === "" ? null : transferNotes.trim(),
                  });
                }}
              >
                Plan transfer
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
